import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import { toast } from "react-toastify";
import {
  FIVE_SLOT_CONFIG,
  JACKPOT_DEFAULTS,
  JACKPOT_TIER_THEMES,
  SIDE_BADGES,
  SLOT_MACHINE_FRAME_THEME,
  getSymbolsByCategory,
  getRandomSymbol,
  calculateWinResult,
} from "./fiveSlotConfig";
import { DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import FiveSlotSymbolIcon from "./FiveSlotSymbolIcon";
import FiveSlotPaytableModal from "./FiveSlotPaytableModal";
import FiveSlotDepositModal from "./FiveSlotDepositModal";
import FiveSlotWinModal from "./FiveSlotWinModal";
import { slotAudio } from "./slotAudio";

export const FiveSlotMachineDetailCard = ({
  lotteryData,
  account,
  balance: parentBalance,
  win: parentWin,
  setBalance: setParentBalance,
  setWin: setParentWin,
}) => {
  const category =
    lotteryData?.slotCategory ||
    (lotteryData?.name?.toLowerCase().includes("crypto") ? "crypto" : "fiat");
  const currency = lotteryData?.currency || DEFAULT_CURRENCY || "POL";
  const activeSymbols = getSymbolsByCategory(category);

  // Game States
  const [balance, setBalance] = useState(() => {
    const init = parseFloat(parentBalance);
    return !isNaN(init) && init > 0 ? init.toFixed(2) : "1250.00";
  });

  const [currentWin, setCurrentWin] = useState("0.00");
  const [betIndex, setBetIndex] = useState(2); // default: 5.00
  const betAmount = FIVE_SLOT_CONFIG.betSteps[betIndex] || 5.0;

  // 5-Reel Symbol Display State: Each reel has [topSymbol, mainSymbol, bottomSymbol]
  const [reelDisplay, setReelDisplay] = useState([
    [activeSymbols[0], activeSymbols[1], activeSymbols[2]],
    [activeSymbols[2], activeSymbols[3], activeSymbols[4]],
    [activeSymbols[4], activeSymbols[0], activeSymbols[1]],
    [activeSymbols[1], activeSymbols[2], activeSymbols[3]],
    [activeSymbols[3], activeSymbols[4], activeSymbols[0]],
  ]);

  const [isSpinning, setIsSpinning] = useState(false);
  const [spinningReels, setSpinningReels] = useState([false, false, false, false, false]);
  const [isAutoSpinning, setIsAutoSpinning] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Bonus & Free Spins
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0);
  const [totalFreeSpins, setTotalFreeSpins] = useState(10);

  // Jackpots (Mega, Major, Mini)
  const [jackpots, setJackpots] = useState({
    mega: parseFloat(lotteryData?.jackpots?.mega?.amount) || JACKPOT_DEFAULTS.mega,
    major: parseFloat(lotteryData?.jackpots?.major?.amount) || JACKPOT_DEFAULTS.major,
    mini: parseFloat(lotteryData?.jackpots?.mini?.amount) || JACKPOT_DEFAULTS.mini,
  });

  // Modals
  const [paytableOpen, setPaytableOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [winModalOpen, setWinModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Sync parent balance if provided
  useEffect(() => {
    if (setParentBalance) setParentBalance(balance);
  }, [balance, setParentBalance]);

  useEffect(() => {
    if (setParentWin) setParentWin(currentWin);
  }, [currentWin, setParentWin]);

  // Jackpot progressive ticker effect
  useEffect(() => {
    const interval = setInterval(() => {
      setJackpots((prev) => ({
        mega: prev.mega + 0.15,
        major: prev.major + 0.05,
        mini: prev.mini + 0.01,
      }));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Quick Bet Adjusters
  const handleDecreaseBet = () => {
    slotAudio.playClick();
    if (betIndex > 0) {
      setBetIndex((prev) => prev - 1);
    }
  };

  const handleIncreaseBet = () => {
    slotAudio.playClick();
    if (betIndex < FIVE_SLOT_CONFIG.betSteps.length - 1) {
      setBetIndex((prev) => prev + 1);
    }
  };

  const handleToggleSound = () => {
    const muted = slotAudio.toggleMute();
    setIsMuted(muted);
    toast.info(muted ? "Sound Muted" : "Sound Enabled", { autoClose: 1200 });
  };

  // Main 5-Reel Spin Logic
  const handleSpin = useCallback(() => {
    if (isSpinning) return;

    const currentBal = parseFloat(balance);
    const isFreeSpin = freeSpinsRemaining > 0;

    if (!isFreeSpin && currentBal < betAmount) {
      toast.error("Insufficient balance! Please deposit funds.", { toastId: "insufficient-funds" });
      setIsAutoSpinning(false);
      setDepositOpen(true);
      return;
    }

    // Deduct bet if not a free spin
    if (!isFreeSpin) {
      setBalance((prev) => (parseFloat(prev) - betAmount).toFixed(2));
    } else {
      setFreeSpinsRemaining((prev) => prev - 1);
    }

    setIsSpinning(true);
    setSpinningReels([true, true, true, true, true]);
    setCurrentWin("0.00");
    setLastResult(null);

    // Pick 5 final center symbols from active 5-symbol set
    const finalCenter = [
      getRandomSymbol(category),
      getRandomSymbol(category),
      getRandomSymbol(category),
      getRandomSymbol(category),
      getRandomSymbol(category),
    ];

    // Generate full final reels [top, center, bottom] for all 5 columns
    const finalReels = finalCenter.map((centerSym) => [
      getRandomSymbol(category),
      centerSym,
      getRandomSymbol(category),
    ]);

    // Fast animation interval for spinning reels
    const spinInterval = setInterval(() => {
      slotAudio.playSpinTick();
      setReelDisplay(() => {
        return [
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
        ];
      });
    }, 90);

    // Step 1: Stop Reel 1 (650ms)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, true, true, true, true]);
      setReelDisplay((prev) => [finalReels[0], prev[1], prev[2], prev[3], prev[4]]);
    }, 650);

    // Step 2: Stop Reel 2 (1050ms)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, false, true, true, true]);
      setReelDisplay((prev) => [finalReels[0], finalReels[1], prev[2], prev[3], prev[4]]);
    }, 1050);

    // Step 3: Stop Reel 3 (1450ms)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, false, false, true, true]);
      setReelDisplay((prev) => [finalReels[0], finalReels[1], finalReels[2], prev[3], prev[4]]);
    }, 1450);

    // Step 4: Stop Reel 4 (1850ms)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, false, false, false, true]);
      setReelDisplay((prev) => [finalReels[0], finalReels[1], finalReels[2], finalReels[3], prev[4]]);
    }, 1850);

    // Step 5: Stop Reel 5 (2250ms) & evaluate result
    setTimeout(() => {
      slotAudio.playReelStop();
      clearInterval(spinInterval);
      setSpinningReels([false, false, false, false, false]);
      setReelDisplay(finalReels);
      setIsSpinning(false);

      // Evaluate Win on Center Line
      const outcome = calculateWinResult(finalCenter, betAmount, isFreeSpin, jackpots);
      setLastResult(outcome);

      if (outcome.isWin) {
        if (outcome.type === "jackpot") {
          slotAudio.playJackpot();
        } else {
          slotAudio.playWin();
        }

        const winFormatted = outcome.amount.toFixed(2);
        setCurrentWin(winFormatted);
        setBalance((prev) => (parseFloat(prev) + outcome.amount).toFixed(2));

        if (outcome.type === "freespins") {
          const spinsWon = outcome.freeSpinsWon || 5;
          setFreeSpinsRemaining((prev) => prev + spinsWon);
          setTotalFreeSpins(spinsWon);
          toast.success(`🎰 ${spinsWon} FREE SPINS TRIGGERED!`, { autoClose: 3500 });
        }

        // Show celebration modal for Jackpots & Big Wins
        if (outcome.type === "jackpot" || outcome.amount >= betAmount * 10) {
          setWinModalOpen(true);
        } else {
          toast.success(`You Won ${currency} ${winFormatted}!`, { autoClose: 2000 });
        }
      }
    }, 2250);
  }, [isSpinning, balance, betAmount, freeSpinsRemaining, category, jackpots, currency]);

  const spinRef = useRef(handleSpin);
  useEffect(() => {
    spinRef.current = handleSpin;
  }, [handleSpin]);

  // Autoplay handler
  useEffect(() => {
    let timer;
    if (isAutoSpinning && !isSpinning) {
      timer = setTimeout(() => {
        if (isAutoSpinning) {
          spinRef.current();
        }
      }, 1200);
    }
    return () => clearTimeout(timer);
  }, [isAutoSpinning, isSpinning]);

  const handleToggleAutoPlay = () => {
    slotAudio.playClick();
    if (isAutoSpinning) {
      setIsAutoSpinning(false);
      toast.info("Autoplay Stopped", { autoClose: 1500 });
    } else {
      setIsAutoSpinning(true);
      toast.success("Autoplay Started!", { autoClose: 1500 });
      if (!isSpinning) {
        handleSpin();
      }
    }
  };

  return (
    <Box
      className="pokies-game-app"
      sx={{
        width: "100%",
        maxWidth: 560,
        margin: "0 auto",
        bgcolor: SLOT_MACHINE_FRAME_THEME.bg,
        borderRadius: SLOT_MACHINE_FRAME_THEME.borderRadius,
        boxShadow: SLOT_MACHINE_FRAME_THEME.boxShadow,
        overflow: "hidden",
        position: "relative",
        border: SLOT_MACHINE_FRAME_THEME.border,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* 1. TOP APP HEADER BAR */}
      <Box
        className="pokies-header-bar"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          bgcolor: SLOT_MACHINE_FRAME_THEME.headerBg,
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          zIndex: 10,
        }}
      >
        {/* Left: Empty spacer */}
        <Stack direction="row" alignItems="center" spacing={1.25} />

        {/* Right: Sound Toggle Button */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={{
              color: "#94a3b8",
              p: 0.75,
              bgcolor: "rgba(255, 255, 255, 0.06)",
              borderRadius: "8px",
              "&:hover": { color: "#ffffff", bgcolor: "rgba(255, 255, 255, 0.15)" },
            }}
          >
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 20 }} /> : <VolumeUpIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Box>

      {/* 2. GAME STAGE */}
      <Box
        className="pokies-stage-wrapper"
        sx={{
          position: "relative",
          backgroundImage: "url(/static/Images/slot_treasures_bg.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          px: { xs: 1.5, sm: 2.25 },
          pt: 1.5,
          pb: 2.5,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(8, 13, 26, 0.3) 0%, rgba(8, 13, 26, 0.7) 60%, rgba(8, 13, 26, 0.95) 100%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          {/* 3. PROGRESSIVE JACKPOT TIERS (MEGA, MAJOR, MINI) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 0.75,
              mb: 1.5,
            }}
          >
            {/* MEGA JACKPOT */}
            <Box
              sx={{
                p: 0.75,
                borderRadius: JACKPOT_TIER_THEMES.mega.borderRadius,
                background: JACKPOT_TIER_THEMES.mega.background,
                border: JACKPOT_TIER_THEMES.mega.border,
                boxShadow: JACKPOT_TIER_THEMES.mega.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mega.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.92rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {Math.floor(jackpots.mega).toLocaleString()}
              </Typography>
            </Box>

            {/* MAJOR JACKPOT */}
            <Box
              sx={{
                p: 0.75,
                borderRadius: JACKPOT_TIER_THEMES.major.borderRadius,
                background: JACKPOT_TIER_THEMES.major.background,
                border: JACKPOT_TIER_THEMES.major.border,
                boxShadow: JACKPOT_TIER_THEMES.major.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.major.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.92rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {Math.floor(jackpots.major).toLocaleString()}
              </Typography>
            </Box>

            {/* MINI JACKPOT */}
            <Box
              sx={{
                p: 0.75,
                borderRadius: JACKPOT_TIER_THEMES.mini.borderRadius,
                background: JACKPOT_TIER_THEMES.mini.background,
                border: JACKPOT_TIER_THEMES.mini.border,
                boxShadow: JACKPOT_TIER_THEMES.mini.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mini.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.92rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {Math.floor(jackpots.mini).toLocaleString()}
              </Typography>
            </Box>
          </Box>

          {/* 4. REELS CASINO FRAME & SIDE PAYLINE INDICATOR PILLARS */}
          <Box
            className="pokies-reels-container"
            sx={{
              position: "relative",
              borderRadius: "16px",
              background: "linear-gradient(180deg, #fef08a 0%, #eab308 50%, #854d0e 100%)",
              p: "5px",
              boxShadow:
                "0 8px 30px rgba(0, 0, 0, 0.8), inset 0 2px 6px rgba(255, 255, 255, 0.8), 0 0 20px rgba(234, 179, 8, 0.4)",
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                borderRadius: "12px",
                bgcolor: "#050b14",
                border: "2px solid #78350f",
                display: "flex",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* LEFT SIDE PAYLINE INDICATOR STRIP */}
              <Box
                sx={{
                  width: { xs: 20, sm: 24 },
                  bgcolor: "#09101d",
                  borderRight: "1px solid rgba(234, 179, 8, 0.4)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  py: 0.5,
                  alignItems: "center",
                }}
              >
                {SIDE_BADGES.map((b, i) => (
                  <Box
                    key={`l-${i}`}
                    sx={{
                      width: { xs: 14, sm: 17 },
                      height: { xs: 14, sm: 17 },
                      borderRadius: "3px",
                      bgcolor: b.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.62rem" },
                        fontWeight: 900,
                        color: "#ffffff",
                        lineHeight: 1,
                      }}
                    >
                      {b.num}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* 5 SLOT REELS GRID */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: "2px",
                  bgcolor: "#020617",
                  position: "relative",
                }}
              >
                {/* CENTER PAYLINE INDICATOR LINE */}
                <Box
                  className="pokies-center-payline"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    height: 2,
                    background: "linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #ef4444 100%)",
                    transform: "translateY(-50%)",
                    zIndex: 6,
                    pointerEvents: "none",
                    boxShadow: "0 0 8px #f59e0b",
                    opacity: 0.65,
                  }}
                />

                {/* 5 REEL COLUMNS */}
                {[0, 1, 2, 3, 4].map((reelIdx) => {
                  const isThisReelSpinning = spinningReels[reelIdx];
                  const reelSymbols = reelDisplay[reelIdx];

                  return (
                    <Box
                      key={`reel-${reelIdx}`}
                      sx={{
                        bgcolor: "#090f1d",
                        borderRight: reelIdx < 4 ? "1.5px solid rgba(234, 179, 8, 0.4)" : "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-around",
                        py: 0.75,
                        minHeight: 230,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Top Symbol (dimmed) */}
                      <Box
                        sx={{
                          opacity: 0.5,
                          transform: "scale(0.85)",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                        }}
                      >
                        <FiveSlotSymbolIcon symbol={reelSymbols?.[0]} size="sm" />
                      </Box>

                      {/* Main Center Symbol (Highlighted) */}
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 5,
                          py: 0.5,
                          filter: isThisReelSpinning ? "blur(4px)" : "none",
                          transform: isThisReelSpinning ? "scale(0.95)" : "scale(1)",
                          transition: "filter 0.15s ease, transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        }}
                      >
                        <FiveSlotSymbolIcon
                          symbol={reelSymbols?.[1]}
                          size="md"
                          isWinning={
                            !isSpinning &&
                            lastResult?.isWin &&
                            (lastResult?.winningIndices?.includes(reelIdx) ||
                              (!lastResult?.winningIndices?.length && true))
                          }
                        />
                      </Box>

                      {/* Bottom Symbol (dimmed) */}
                      <Box
                        sx={{
                          opacity: 0.5,
                          transform: "scale(0.85)",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                        }}
                      >
                        <FiveSlotSymbolIcon symbol={reelSymbols?.[2]} size="sm" />
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* RIGHT SIDE PAYLINE INDICATOR STRIP */}
              <Box
                sx={{
                  width: { xs: 20, sm: 24 },
                  bgcolor: "#09101d",
                  borderLeft: "1px solid rgba(234, 179, 8, 0.4)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  py: 0.5,
                  alignItems: "center",
                }}
              >
                {SIDE_BADGES.map((b, i) => (
                  <Box
                    key={`r-${i}`}
                    sx={{
                      width: { xs: 14, sm: 17 },
                      height: { xs: 14, sm: 17 },
                      borderRadius: "3px",
                      bgcolor: b.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.62rem" },
                        fontWeight: 900,
                        color: "#ffffff",
                        lineHeight: 1,
                      }}
                    >
                      {b.num}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* 5. DASHBOARD BAR (BALANCE | BET | WIN | FREE SPINS) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.1fr 1.3fr 1fr 1.1fr",
              gap: 0.75,
              p: 1.25,
              borderRadius: "12px",
              bgcolor: "#0b1220",
              border: "1.5px solid #eab308",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.6)",
              mb: 2,
            }}
          >
            {/* BALANCE */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  color: "#94a3b8",
                  letterSpacing: "0.5px",
                }}
              >
                BALANCE
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.9rem" },
                  fontWeight: 900,
                  color: "#fef08a",
                  fontFamily: "monospace",
                }}
              >
                {currency} {parseFloat(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>

            {/* BET STEPPER */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  color: "#94a3b8",
                  letterSpacing: "0.5px",
                }}
              >
                BET
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
                sx={{ mt: "1px" }}
              >
                <Box
                  onClick={handleDecreaseBet}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "4px",
                    bgcolor: "#1e293b",
                    border: "1px solid rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#334155" },
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14, color: "#ffffff" }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "0.78rem", sm: "0.9rem" },
                    fontWeight: 900,
                    color: "#ffffff",
                    fontFamily: "monospace",
                    minWidth: 48,
                  }}
                >
                  {currency} {betAmount.toFixed(2)}
                </Typography>
                <Box
                  onClick={handleIncreaseBet}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "4px",
                    bgcolor: "#1e293b",
                    border: "1px solid rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#334155" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14, color: "#ffffff" }} />
                </Box>
              </Stack>
            </Box>

            {/* WIN AMOUNT */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  color: "#94a3b8",
                  letterSpacing: "0.5px",
                }}
              >
                WIN
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.9rem" },
                  fontWeight: 900,
                  color: parseFloat(currentWin) > 0 ? "#4ade80" : "#fef08a",
                  fontFamily: "monospace",
                  textShadow: parseFloat(currentWin) > 0 ? "0 0 8px #22c55e" : "none",
                }}
              >
                {currency} {currentWin}
              </Typography>
            </Box>

            {/* FREE SPINS BOX */}
            <Box
              sx={{
                textAlign: "center",
                borderRadius: "6px",
                background:
                  freeSpinsRemaining > 0
                    ? "linear-gradient(135deg, #f97316 0%, #dc2626 100%)"
                    : "linear-gradient(135deg, #78350f 0%, #451a03 100%)",
                border: "1px solid #f59e0b",
                p: "2px",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                <Typography sx={{ fontSize: "0.75rem" }}>🎁</Typography>
                <Typography
                  sx={{
                    fontSize: "0.52rem",
                    fontWeight: 900,
                    color: "#fef08a",
                    lineHeight: 1,
                  }}
                >
                  FREE SPINS
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 900,
                  color: "#ffffff",
                  lineHeight: 1.1,
                  fontFamily: "monospace",
                }}
              >
                {freeSpinsRemaining} / {totalFreeSpins}
              </Typography>
            </Box>
          </Box>

          {/* 6. MAIN ACTION BAR (PAYTABLE | [-] | BIG 3D SPIN | [+] | AUTOPLAY) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 0.5, sm: 1 },
              gap: 1.25,
            }}
          >
            {/* PAYTABLE BUTTON */}
            <Box
              onClick={() => {
                slotAudio.playClick();
                setPaytableOpen(true);
              }}
              sx={{
                flex: 1.3,
                height: 52,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #1d4ed8 0%, #1e40af 50%, #172554 100%)",
                border: "1.5px solid #60a5fa",
                boxShadow: "0 4px 12px rgba(29, 78, 216, 0.4), inset 0 1px 2px rgba(255,255,255,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.15)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <FormatListBulletedIcon sx={{ color: "#ffffff", fontSize: 20 }} />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.92rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                PAYTABLE
              </Typography>
            </Box>

            {/* QUICK BET MINUS */}
            <Box
              onClick={handleDecreaseBet}
              sx={{
                width: 48,
                height: 52,
                borderRadius: "14px",
                bgcolor: "#0f172a",
                border: "1.5px solid #334155",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 4px 8px rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "#1e293b", borderColor: "#64748b" },
                "&:active": { transform: "scale(0.92)" },
              }}
            >
              <RemoveIcon sx={{ color: "#ffffff", fontSize: 26 }} />
            </Box>

            {/* BIG 3D RED SPIN BUTTON */}
            <Box
              onClick={handleSpin}
              sx={{
                width: { xs: 92, sm: 104 },
                height: { xs: 92, sm: 104 },
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fef08a 0%, #eab308 50%, #854d0e 100%)",
                p: "5px",
                boxShadow:
                  "0 6px 22px rgba(0, 0, 0, 0.8), 0 0 28px rgba(239, 68, 68, 0.6), inset 0 2px 4px rgba(255,255,255,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSpinning ? "not-allowed" : "pointer",
                transform: isSpinning ? "scale(0.95)" : "scale(1)",
                transition: "all 0.15s ease",
                "&:hover": {
                  boxShadow:
                    "0 8px 32px rgba(0, 0, 0, 0.9), 0 0 38px rgba(239, 68, 68, 0.9)",
                },
                "&:active": {
                  transform: "scale(0.92)",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: isSpinning
                    ? "radial-gradient(circle at 35% 30%, #f87171 0%, #dc2626 55%, #7f1d1d 100%)"
                    : "radial-gradient(circle at 35% 30%, #ef4444 0%, #b91c1c 55%, #450a0a 100%)",
                  border: "2.5px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "inset 0 4px 10px rgba(255,255,255,0.6), inset 0 -4px 10px rgba(0,0,0,0.8)",
                  animation: isSpinning ? "spinButtonPulse 0.5s infinite alternate" : "none",
                  "@keyframes spinButtonPulse": {
                    "0%": { filter: "brightness(1)" },
                    "100%": { filter: "brightness(1.3)" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    fontSize: { xs: "1.55rem", sm: "1.85rem" },
                    fontWeight: 900,
                    color: "#ffffff",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    textShadow:
                      "0 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(254, 240, 138, 0.8)",
                    transform: "translateY(-1px)",
                  }}
                >
                  {isSpinning ? "..." : "SPIN"}
                </Typography>
              </Box>
            </Box>

            {/* QUICK BET PLUS */}
            <Box
              onClick={handleIncreaseBet}
              sx={{
                width: 48,
                height: 52,
                borderRadius: "14px",
                bgcolor: "#0f172a",
                border: "1.5px solid #334155",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 4px 8px rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "#1e293b", borderColor: "#64748b" },
                "&:active": { transform: "scale(0.92)" },
              }}
            >
              <AddIcon sx={{ color: "#ffffff", fontSize: 26 }} />
            </Box>

            {/* AUTOPLAY BUTTON */}
            <Box
              onClick={handleToggleAutoPlay}
              sx={{
                flex: 1.3,
                height: 52,
                borderRadius: "14px",
                background: isAutoSpinning
                  ? "linear-gradient(180deg, #dc2626 0%, #991b1b 50%, #450a0a 100%)"
                  : "linear-gradient(180deg, #1d4ed8 0%, #1e40af 50%, #172554 100%)",
                border: isAutoSpinning ? "1.5px solid #f87171" : "1.5px solid #60a5fa",
                boxShadow: isAutoSpinning
                  ? "0 4px 12px rgba(220, 38, 38, 0.6)"
                  : "0 4px 12px rgba(29, 78, 216, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.15)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <AutorenewIcon
                sx={{
                  color: "#ffffff",
                  fontSize: 20,
                  animation: isAutoSpinning ? "spinIcon 1s linear infinite" : "none",
                  "@keyframes spinIcon": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.92rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                {isAutoSpinning ? "STOP" : "AUTOPLAY"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 7. MODALS & POPUPS */}
      <FiveSlotPaytableModal
        open={paytableOpen}
        onClose={() => setPaytableOpen(false)}
        activeCategory={category}
        currency={currency}
      />
      <FiveSlotDepositModal
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        currentBalance={balance}
        onDepositSuccess={(newBal) => setBalance(newBal)}
        currency={currency}
      />
      <FiveSlotWinModal
        open={winModalOpen}
        onClose={() => setWinModalOpen(false)}
        result={lastResult}
        currencySymbol={currency}
      />
    </Box>
  );
};

export default FiveSlotMachineDetailCard;
